import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import {
    BuildingOffice2Icon,
    MagnifyingGlassIcon,
    PlusIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { FloatingMenu } from '../FloatingMenu';

interface SearchPickerOption {
    key: string | number;
    title: string | null;
    subline?: string | null;
    meta?: string;
    onPick: () => void;
}

export interface SearchPickerStatus {
    text: string;
    isError?: boolean;
}

const RESULTS_MAX_HEIGHT = 220;

function OptionMeta({ meta }: { meta?: string }) {
    if (!meta) return null;
    return <span className="font-mono text-[11px] text-gray-400 shrink-0">{meta}</span>;
}

export function PickerOptionButton({ option }: { option: SearchPickerOption }) {
    return (
        <button
            type="button"
            onClick={option.onPick}
            className="w-full px-3 h-11 flex items-center gap-2.5 text-left hover:bg-gray-50"
        >
            <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
            <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-gray-900 truncate">
                    {option.title}
                </span>
                {option.subline && (
                    <span className="block text-[11px] text-gray-400 truncate">
                        {option.subline}
                    </span>
                )}
            </span>
            <OptionMeta meta={option.meta} />
        </button>
    );
}

interface SearchPickerInputProps {
    search: string;
    onSearchChange: (value: string) => void;
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
    placeholder: string;
}

export function SearchPickerInput({
    search,
    onSearchChange,
    open,
    onOpen,
    onClose,
    placeholder
}: SearchPickerInputProps) {
    return (
        <span
            className={`h-10 border rounded-lg px-3 flex items-center gap-2 ${
                open ? 'bg-white border-gray-800 ring-2 ring-gray-200' : 'bg-white border-gray-300'
            }`}
        >
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
            <input
                type="text"
                value={search}
                onFocus={onOpen}
                onChange={(e) => {
                    onSearchChange(e.target.value);
                    onOpen();
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Escape') return;
                    // Consume Escape while the dropdown is open so it doesn't also close the host modal.
                    if (open) e.stopPropagation();
                    onClose();
                }}
                placeholder={placeholder}
                aria-label={placeholder}
                className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
            />
        </span>
    );
}

interface SearchPickerMenuProps {
    options: SearchPickerOption[];
    status: SearchPickerStatus | null;
    action?: { label: string; onClick: () => void };
}

function SearchPickerMenu({ options, status, action }: SearchPickerMenuProps) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0_12px_28px_rgba(0,0,0,0.14)] overflow-hidden">
            <OverlayScrollArea contentClassName="max-h-[220px] py-1" trackInsetPx={4}>
                {status && (
                    <div
                        className={`px-3 py-2 text-[12px] ${
                            status.isError ? 'text-red-600' : 'text-gray-400'
                        }`}
                    >
                        {status.text}
                    </div>
                )}
                {!status &&
                    options.map((option) => (
                        <PickerOptionButton key={option.key} option={option} />
                    ))}
                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        className="w-full px-3 h-11 flex items-center gap-2.5 text-left hover:bg-gray-50 border-t border-gray-100 text-[13px] font-semibold text-gray-800"
                    >
                        <PlusIcon className="h-4 w-4 text-gray-400 shrink-0" />
                        {action.label}
                    </button>
                )}
            </OverlayScrollArea>
        </div>
    );
}

type SearchPickerFieldProps = SearchPickerInputProps & SearchPickerMenuProps;

// Portals via FloatingMenu since its callers sit in scroll containers.
export function SearchPickerField({ options, status, action, ...input }: SearchPickerFieldProps) {
    const triggerRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <div ref={triggerRef}>
                <SearchPickerInput {...input} />
            </div>

            <FloatingMenu
                open={input.open}
                triggerRef={triggerRef}
                onClose={input.onClose}
                align="left"
                matchTriggerWidth
                estimatedHeight={RESULTS_MAX_HEIGHT}
            >
                <SearchPickerMenu options={options} status={status} action={action} />
            </FloatingMenu>
        </>
    );
}

export interface SearchPickerRow {
    key: string | number;
    title: string;
    titleMuted?: boolean;
    subline?: string | null;
    meta?: string;
    badge?: ReactNode;
    onNavigate?: () => void;
    onRemove?: () => void;
    removeLabel?: string;
}

function RemoveRowButton({ label, onRemove }: { label?: string; onRemove: () => void }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onRemove();
            }}
            aria-label={label}
            className="w-6 h-6 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-900 shrink-0"
        >
            <XMarkIcon className="h-3.5 w-3.5" />
        </button>
    );
}

function SelectedRow({ row }: { row: SearchPickerRow }) {
    const { onNavigate, onRemove } = row;
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (!onNavigate) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onNavigate();
    };

    return (
        <div
            role={onNavigate ? 'button' : undefined}
            tabIndex={onNavigate ? 0 : undefined}
            onClick={onNavigate}
            onKeyDown={handleKeyDown}
            className={`h-10 px-3 flex items-center justify-between gap-2 transition-colors ${
                onNavigate ? 'hover:bg-gray-50 cursor-pointer' : ''
            }`}
        >
            <span className="flex items-center gap-2 min-w-0">
                <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="min-w-0">
                    <span
                        className={`block text-[13px] font-semibold truncate ${
                            row.titleMuted ? 'text-gray-400' : 'text-gray-800'
                        }`}
                    >
                        {row.title}
                    </span>
                    {row.subline && (
                        <span className="block text-[11px] text-gray-400 truncate">
                            {row.subline}
                        </span>
                    )}
                </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
                <OptionMeta meta={row.meta} />
                {row.badge}
                {onRemove && <RemoveRowButton label={row.removeLabel} onRemove={onRemove} />}
            </span>
        </div>
    );
}

export function SearchPickerList({
    rows,
    emptyText
}: {
    rows: SearchPickerRow[];
    emptyText: string;
}) {
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
            {rows.length === 0 ? (
                <div className="px-3 py-2.5 text-[12px] text-gray-400">{emptyText}</div>
            ) : (
                rows.map((row) => <SelectedRow key={row.key} row={row} />)
            )}
        </div>
    );
}
